import { ViewportScroller } from '@angular/common';
import {
  AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, Inject, Input, OnChanges,
  OnDestroy, OnInit, SimpleChanges
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable, switchMap, take, tap, zip } from 'rxjs';

import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { BillingFacade } from '@billing/billing.facade';
import { BookingFacade } from '@booking/booking.facade';
import { BookingBaseContainer } from '@booking/containers/booking-base.container';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { PaxFacade } from '@pax/pax.facade';
import { PaxFormService } from '@pax/services/pax-form.service';
import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { BookingAmplitudeService } from '@booking/services/booking-amplitude.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { AppFacade } from '@app/app.facade';
import { AssistanceFacade } from '@shared/travel-assistance/assistance.facade';
import { FullValidTravelersPipe } from '@pax/pipes/full-valid-travelers.pipe';
import { AvoService } from '@avo/services/avo.service';
import { TravelerListVmPipe } from '@pax/pipes/traveler-list-vm.pipe';
import { TravelerInfoType } from '@pax/entities/traveler-info';
import {
  ActivitiesValidateResponse, ActivityFareBookParamsVM, ExtraInformationVM
} from '@activities/entities/activities-booking';
import { BOOKING_FORM_INPUTS } from '@booking/constants/forms.constants';
import { DEFAULT_EMAIL_CONTROL, DEFAULT_PHONE_CONTROL } from '@forms/contact-form/constants/contact-form.constants';
import { environment as ENV } from '@environment';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { SummaryCardOption, SummaryCardType } from '@design-system/components/organisms/summary-card/entities/summary-card.types';
import { PriceType, ProductOption } from '@activities/activities-results/entities/activities-results';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { productResultsDetailOutletRoute } from '@app/constants/app-routing.constants';
import { bookingDetailRoute } from '@booking/constants/booking-routes.constants';
import { getActivitiesBookingFareParam } from '@activities/activities-results/mappers/activities-routing.mapper';
import { getFareAttributes } from '@activities/mappers/fare-attributes.mapper';
import {
  TypographyFontFamily, TypographyFontSize, TypographyColor
} from '@design-system/components/atoms/typography/entities/typography.types';
import { activitiesBookPayloadMapper } from '@activities/mappers/activities-book-payload.mapper';
import { BookingArrangementVM, SubmitBookingArrangement } from '@booking/entities/booking-arrangement';
import { NavigationService } from '@app/services/navigation.service';
import { getExtraInformationSection } from '@activities/mappers/extra-questions.mapper';
import { SessionStorageService } from '@app/services/session-storage.service';
import { ProductProperties } from '@app/modules/avo/interface/trigger-props';

@Component({
  selector: 'app-booking-activities',
  templateUrl: './booking-activities.container.html',
  styleUrls: ['../booking-base.container.sass'],
  standalone: false
})
export class BookingActivitiesContainer extends BookingBaseContainer implements
  OnInit, OnChanges, AfterViewInit, AfterViewChecked, OnDestroy {

  @Input() validation: ActivitiesValidateResponse;

  public bookingActivitiesForm: UntypedFormGroup;

  public readonly outletRoute = productResultsDetailOutletRoute;

  public readonly travelerType: TravelerInfoType = TravelerInfoType.PERSON;
  public readonly policiesType: SearchBy = SearchBy.ACTIVITIES;

  public readonly summaryCardType = SummaryCardType;

  public readonly icons = Icon;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly transloco: TranslocoService,
    private readonly navigationService: NavigationService,
    private readonly sessionStorageService: SessionStorageService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected cdRef: ChangeDetectorRef,
    protected appFacade: AppFacade,
    protected viewportScroller: ViewportScroller,
    protected facade: BookingFacade,
    protected billingFacade: BillingFacade,
    protected paxFacade: PaxFacade,
    protected paxFormService: PaxFormService,
    protected scrollFormService: ScrollFormService,
    protected propertiesAmplitudeService: PropertiesAmplitudeService,
    protected bookingAmplitudeService: BookingAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected assistanceFacade: AssistanceFacade,
    protected fullValidTravelersPipe: FullValidTravelersPipe,
    protected avoService: AvoService,
    protected travelerListVmPipe: TravelerListVmPipe,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super(
      route, router, cdRef, appFacade, viewportScroller,
      facade, billingFacade, paxFacade, paxFormService, scrollFormService,
      propertiesAmplitudeService, bookingAmplitudeService, triggerAmplitudeService,
      assistanceFacade, fullValidTravelersPipe, avoService, travelerListVmPipe
    );
    this.travelerBookingProvider = TravelBookingType.ACTIVITIES;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.user && this.bookingActivitiesForm) {
      this.setUserData();
    }
  }

  ngOnInit(): void {
    this.createForm();
    this.listenFormChanges(this.bookingActivitiesForm);
    this.isLoadingBookingDelay$ = this.isLoadingBookingDelaySubscription();
    // Temporal logic until Netactica solution
    this.configDefaultByInfoType(this.travelerBookingProvider, this.startDate);
    this.listenToAssistanceSearchCall(
      this.validation,
      this.validation?.Products?.TotalPrice?.CurrencyCode,
      this.route.snapshot.paramMap
    );
  }

  ngAfterViewInit(): void {
    if (this.user && this.bookingActivitiesForm) {
      this.setUserData();
    }
  }

  ngAfterViewChecked(): void {
    this.checkPaxSection();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get products(): ProductOption[] {
    return this.validation?.Products?.ProductsDetails ?? [];
  }

  get isPerService(): boolean {
    return this.products?.[0]?.Fares?.[0]?.PriceType === PriceType.PerService;
  }
  
  get extraInformation$(): Observable<ExtraInformationVM[]> {
    return combineLatest([this.passengerListUI$, this.paxSectionReady$]).pipe(
      map(([passengerListUI, paxSectionReady]) => {
        if (paxSectionReady && this.validation?.ExtraQuestions?.length) {
          return getExtraInformationSection(passengerListUI, this.validation);
        } else {
          return [];
        }
      })
    );
  }

  // Temporal logic until Netactica solution
  get startDate(): string {
    return this.faresVm?.[0]?.SelectedDate;
  }

  // Temporal logic until Netactica solution
  get faresVm(): ActivityFareBookParamsVM[] {
    return getActivitiesBookingFareParam(this.navigationService.params?.rateId);
  }

  // Temporal logic until Netactica solution
  get activityName(): string {
    return this.sessionStorageService.getActivityName();
  }

  public summaryOptions(isDetailed: boolean): SummaryCardOption[] {
    const options = getFareAttributes(this.validation, (isDetailed ? this.faresVm : []));
    return options.map(option => ({
      ...option,
      Title: this.transloco.translate(option.Title),
      Description: this.transloco.translate(option.Description),
    }));
  }

  public goToDetail(): void {
    this.navigationService.navigateOutlet(this.outletRoute, bookingDetailRoute, {
      isActivitiesDetail: true,
      activitiesValidation: this.validation,
      faresVm: this.faresVm,
      activityName: this.activityName
    });
  }

  public setShowBillingModule(value: boolean): void {
    this.showBillingModule = value;
    this.resetbillingForm(this.bookingActivitiesForm);
  }

  public submitBookingOptions(isDisabled: boolean): void {
    this.submitToBookServiceSubscription$(this.bookingActivitiesForm.value).pipe(
      take(1),
      tap(({ props, trigger }) => {
        this.isSubmitted = true;
        if (isDisabled) {
          this.validateAllFormFields(this.bookingActivitiesForm);
          this.validateSectionFormToScroll(this.bookingActivitiesForm);
        } else {
          this.submitByBookServiceAction(
            { props, trigger }, this.bookingActivitiesForm, this.travelerBookingProvider, SearchBy.ACTIVITIES
          );
        }
      })
    ).subscribe().unsubscribe();
  }

  private submitToBookServiceSubscription$(arrangement: BookingArrangementVM): Observable<SubmitBookingArrangement> {
    return zip(
      this.billingFacade.defaultBillingInfo$.pipe(take(1)),
      this.bookingReady$.pipe(take(1)),
      this.passengerListUI$,
      this.propertiesAmplitudeService.searchProperties$(SearchBy.ACTIVITIES, this.navigationService.params.searchId)
        .pipe(take(1)),
    ).pipe(
      switchMap(([defaultBillingInfo, isReadyBook, passengerListUI, searchProps]) => {
        // Temporal logic until Netactica solution
        const productProps: ProductProperties = {
          TRAVEL_PRODUCT_NAME: this.activityName,
          TRAVEL_PRODUCT_PRICE: this.validation?.Products?.TotalPrice?.Total
        }
        return this.bookingAmplitudeService.activitiesCheckoutTriggerAmplitudeEntity$(
          this.navigationService.params.optionId, 
          searchProps,
          productProps
        ).pipe(
          map(trigger => ({
            props: activitiesBookPayloadMapper(this.validation, passengerListUI, arrangement, defaultBillingInfo),
            trigger
          }))
        )
      }
      )
    )
  }

  private createForm(): void {
    this.bookingActivitiesForm = new UntypedFormGroup({
      [BOOKING_FORM_INPUTS.TRAVELERS]: this.fb.array([]),
      [BOOKING_FORM_INPUTS.PHONE]: DEFAULT_PHONE_CONTROL,
      [BOOKING_FORM_INPUTS.EMAIL]: DEFAULT_EMAIL_CONTROL,
      [BOOKING_FORM_INPUTS.COMMENTS]: new UntypedFormControl('', [
        Validators.maxLength(500),
      ]),
      [BOOKING_FORM_INPUTS.TERMS]: new UntypedFormControl(null, [
        Validators.required,
        Validators.minLength(3)
      ]),
      [BOOKING_FORM_INPUTS.BILLING]: this.fb.group({})
    });
    if (this.individualTerms) {
      this.bookingActivitiesForm.addControl(
        BOOKING_FORM_INPUTS.CIVITATIS_TERMS,
        new UntypedFormControl(null, [
          Validators.required,
          Validators.minLength(3)
        ])
      );
      this.bookingActivitiesForm.addControl(BOOKING_FORM_INPUTS.EXTRA_TERMS, new UntypedFormControl(null, [
        Validators.required, Validators.minLength(3)
      ]));
    }
  }

  private setUserData(): void {
    this.bookingActivitiesForm.patchValue({
      [BOOKING_FORM_INPUTS.EMAIL]: this.user.email ? this.user.email : '',
      [BOOKING_FORM_INPUTS.PHONE]: this.user.phone ?
        `${this.user.country_code ? this.user.country_code.replace('+', '') :
          ENV.countryInfo.phone_prefix.replace('+', '')}${this.user.phone}` : ''
    });
  }

}
